import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import { useGlobalSearchQuery } from "@graphql/global-search/globalSearch.g";
import { useDebounce } from "@uidotdev/usehooks";
import { ChangeEvent, KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Icon } from "@design-system/Icon/Icon";
import { Input } from "@design-system/Input/Input";
import { Resource } from "@design-system/Resource/Resource";
import { ScrollBox } from "@design-system/ScrollBox/ScrollBox";
import { Text } from "@design-system/Text/Text";
import { User } from "@design-system/User/User";

import { routes } from "@pages/routes";

import { GlobalSearchContext } from "./GlobalSearchContext";

import classes from "./GlobalSearch.module.css";

const SEARCH_DEBOUNCE_TIME = 300;

type SelectValue = string;

type Props = {
  onSelect?: (id: SelectValue) => void;
};

export const GlobalSearch = ({ onSelect }: Props) => {
  const navigate = useNavigate();
  const [reference, setReference] = useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);

  const globalSearchQuery = useGlobalSearchQuery(
    { search: debouncedSearch },
    { enabled: !!debouncedSearch },
  );

  const results = useMemo(() => {
    const clients = globalSearchQuery.data?.clients?.edges.map(
      (edge) => edge?.node,
    );
    const users = globalSearchQuery.data?.users?.edges.map(
      (edge) => edge?.node,
    );
    const employees = globalSearchQuery.data?.clientEmployees?.edges.map(
      (edge) => edge?.node,
    );
    const applications =
      globalSearchQuery.data?.applicationResources?.edges.map(
        (edge) => edge?.node,
      );
    const catalogResources = globalSearchQuery.data?.resources?.edges.map(
      (edge) => edge?.node,
    );

    return {
      clients,
      users,
      employees,
      applications,
      catalogResources,
    };
  }, [globalSearchQuery.data]);

  const isClientsFound = results.clients && results.clients.length > 0;
  const isUsersFound = results.users && results.users.length > 0;
  const isEmployeesFound = results.employees && results.employees.length > 0;
  const isCatalogFound =
    results.catalogResources && results.catalogResources.length > 0;

  const employeesGroupedByTenant = useMemo(() => {
    if (!results.employees) return [];

    const grouped: Record<
      string,
      {
        clientId: string;
        clientName: string;
        employees: {
          employeeId: string;
          name: string;
          email: string;
        }[];
      }
    > = {};

    results.employees.forEach((employee) => {
      if (!employee) return;
      
      const clientId = employee.client?.id;
      const clientName = employee.client?.name;
      
      if (!clientId) return;

      if (!grouped[clientId]) {
        grouped[clientId] = {
          clientId,
          clientName: clientName || "",
          employees: [],
        };
      }

      grouped[clientId].employees.push({
        employeeId: employee.id || "",
        name: employee.name || "",
        email: employee.email || "",
      });
    });

    return Object.values(grouped).sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [results.employees]);

  const applicationsGroupedByTenant = useMemo(() => {
    if (!results.applications) return [];

    const grouped: Record<
      string,
      {
        clientId: string;
        clientName: string;
        apps: {
          clientAppId: string;
          applicationId: string;
          productName: string;
          vendorName: string;
          productDomainName: string;
          signedLogoUrl: string;
          hasLogo: boolean;
        }[];
      }
    > = {};

    results.applications.forEach((application) => {
      application?.applications?.edges.forEach((clientApp) => {
        const clientId = clientApp?.node?.client?.id;
        const clientName = clientApp?.node?.client?.name;
        
        if (!clientId) return;

        if (!grouped[clientId]) {
          grouped[clientId] = {
            clientId,
            clientName: clientName || "",
            apps: [],
          };
        }

        grouped[clientId].apps.push({
          clientAppId: clientApp?.node?.id || "",
          applicationId: application.id,
          productName: application.productName || "",
          vendorName: application.vendorName || "",
          productDomainName: application.productDomainName || "",
          signedLogoUrl: application.publicResource?.signedLogoUrl || "",
          hasLogo: !!application.publicResource?.signedLogoUrl,
        });
      });
    });

    return Object.values(grouped).sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [results.applications]);

  const isApplicationsFound = applicationsGroupedByTenant.length > 0;

  const isEmpty =
    !isClientsFound &&
    !isUsersFound &&
    !isEmployeesFound &&
    !isApplicationsFound &&
    !isCatalogFound;

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // setSearch("");
    }
  }, []);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    elements: {
      reference,
    },
    middleware: [
      flip({
        padding: 12,
      }),
      offset({ mainAxis: 12 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnHover: false,
  });

  const focus = useFocus(context);
  const role = useRole(context, { role: "listbox" });
  const dismiss = useDismiss(context, {
    outsidePress: () => {
      setSearch("");
      return true;
    },
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNavigation, focus, dismiss, role],
  );

  const handleSelect = useCallback(
    (id: string, index: number | null) => {
      onSelect?.(id);
      setSelectedIndex(index);
      setIsOpen(false);
    },
    [onSelect],
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    // Prevent space key from being captured by list navigation when typing
    if (event.key === " ") {
      event.stopPropagation();
    }
  }, []);

  const handleClientClick = (clientId?: string) => {
    if (!clientId) return;

    navigate(
      routes.portal.clients.client.record.$buildPath({ params: { clientId } }),
    );
    setIsOpen(false);
    setSearch("");
  };

  const handleUserClick = (userId?: string) => {
    if (!userId) return;
    navigate(routes.portal.users.user.$buildPath({ params: { userId } }));
    setIsOpen(false);
    setSearch("");
  };

  const handleEmployeeClick = (clientId: string, clientEmployeeId?: string) => {
    if (!clientEmployeeId) return;
    navigate(
      routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$buildPath(
        {
          params: { clientId, clientEmployeeId },
        },
      ),
    );
    setIsOpen(false);
    setSearch("");
  };

  const handleApplicationClick = (clientId: string, applicationId: string) => {
    navigate(
      routes.portal.clients.client.applications.application.dashboard.$buildPath(
        {
          params: {
            clientId,
            applicationId,
          },
        },
      ),
    );
    setIsOpen(false);
    setSearch("");
  };

  const handleCatalogResourceClick = useCallback(
    (searchTerm: string) => {
      navigate(
        routes.portal.administration.applications.$buildPath({
          searchParams: { search: searchTerm },
        }),
        { viewTransition: true },
      );
      setIsOpen(false);
      setSearch("");
    },
    [navigate],
  );

  const dropdownContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, selectedIndex, getItemProps, handleSelect],
  );

  return (
    <>
      <div className={classes.search} ref={(el) => setReference(el)}>
        <Icon icon="faMagnifyingGlass" size="sm" />
        <Input
          {...getReferenceProps({
            type: "text",
            onChange: handleSearch,
            onKeyDown: handleKeyDown,
            value: search,
            placeholder: "Search...",
            "aria-autocomplete": "list",
            // This ensures that the input doesn't preserve min-width on
            // small screens and wraps correctly when there isn't enough space.
            size: 1,
          })}
          transparent
        />
      </div>
      <FloatingPortal>
        <GlobalSearchContext.Provider value={dropdownContext}>
          {isOpen && (
            <FloatingFocusManager
              context={context}
              modal={false}
              order={["reference", "floating"]}
              returnFocus={false}
            >
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, zIndex: 1001 }}
                className={classes.floating}
                {...getFloatingProps()}
              >
                <Card>
                  <ScrollBox stretch>
                    <div className={classes.list}>
                      <FloatingList
                        elementsRef={elementsRef}
                        labelsRef={labelsRef}
                      >
                        <Box direction="column" gap={16} padding="12px">
                          {isClientsFound && (
                            <Box direction="column" gap={12} stretch>
                              <Box gap={6}>
                                <Icon icon="faSuitcase" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Tenants
                                </Text>
                              </Box>
                              {results.clients?.map((client) => (
                                <Clickable
                                  onClick={() => handleClientClick(client?.id)}
                                  outline="xs"
                                  stretch
                                >
                                  <div className={classes.truncatedText}>
                                    <Resource
                                      key={client?.id}
                                      name={client?.name}
                                      domain={client?.domain}
                                    />
                                  </div>
                                </Clickable>
                              ))}
                            </Box>
                          )}

                          {isUsersFound && (
                            <Box direction="column" gap={12} stretch>
                              <Box gap={6}>
                                <Icon icon="faUser" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Staff members
                                </Text>
                              </Box>
                              {results.users?.map((user) => (
                                <Clickable
                                  onClick={() => handleUserClick(user?.id)}
                                  outline="xs"
                                  stretch
                                >
                                  <div className={classes.truncatedText}>
                                    <User
                                      key={user?.id}
                                      name={user?.name}
                                      email={user?.email}
                                    />
                                  </div>
                                </Clickable>
                              ))}
                            </Box>
                          )}

                          {isEmployeesFound && (
                            <Box
                              direction="column"
                              gap={12}
                              stretch
                              fullStretch
                            >
                              <Box gap={6}>
                                <Icon icon="faUserTie" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Users
                                </Text>
                              </Box>
                              {employeesGroupedByTenant.map((tenantGroup) => (
                                <Box key={tenantGroup.clientId} direction="column" gap={8} fullStretch>
                                  <Box gap={6} padding="8px 0 0 12px">
                                    <Text color="textSecondary">
                                      <Icon icon="faSuitcase" size="sm" />
                                    </Text>
                                    <Text
                                      size={12}
                                      weight={700}
                                      color="textSecondary"
                                      uppercase
                                    >
                                      {tenantGroup.clientName}
                                    </Text>
                                  </Box>
                                  <Box direction="column" gap={8} padding="0 0 0 24px" fullStretch>
                                    {tenantGroup.employees.map((employee) => (
                                      <Clickable
                                        key={employee.employeeId}
                                        onClick={() =>
                                          handleEmployeeClick(
                                            tenantGroup.clientId,
                                            employee.employeeId,
                                          )
                                        }
                                        outline="xs"
                                        stretch
                                      >
                                        <div className={classes.truncatedText}>
                                          <User
                                            name={employee.name}
                                            email={employee.email}
                                          />
                                        </div>
                                      </Clickable>
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {isCatalogFound && (
                            <Box
                              direction="column"
                              gap={12}
                              stretch
                              fullStretch
                            >
                              <Box gap={6}>
                                <Icon icon="faBookAtlas" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Application catalog
                                </Text>
                              </Box>
                              {results.catalogResources?.map((resource) => (
                                <Clickable
                                  key={resource?.id}
                                  onClick={() =>
                                    handleCatalogResourceClick(
                                      resource?.productName || debouncedSearch,
                                    )
                                  }
                                  outline="xs"
                                  stretch
                                >
                                  <div className={classes.truncatedText}>
                                    <Resource
                                      name={resource?.productName ?? undefined}
                                      subtext={resource?.vendorName ?? undefined}
                                      domain={resource?.productDomainName ?? undefined}
                                      signedLogoUrl={resource?.signedLogoUrl ?? undefined}
                                    />
                                  </div>
                                </Clickable>
                              ))}
                            </Box>
                          )}

                          {isApplicationsFound && (
                            <Box
                              direction="column"
                              gap={12}
                              stretch
                              fullStretch
                            >
                              <Box gap={6}>
                                <Icon icon="faShapes" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Discovered applications
                                </Text>
                              </Box>
                              {applicationsGroupedByTenant.map((tenantGroup) => (
                                <Box key={tenantGroup.clientId} direction="column" gap={8} fullStretch>
                                  <Box gap={6} padding="8px 0 0 12px">
                                    <Text color="textSecondary">
                                      <Icon icon="faSuitcase" size="sm" />
                                    </Text>
                                    <Text
                                      size={12}
                                      weight={700}
                                      color="textSecondary"
                                      uppercase
                                    >
                                      {tenantGroup.clientName}
                                    </Text>
                                  </Box>
                                  <Box direction="column" gap={8} padding="0 0 0 24px" fullStretch>
                                    {tenantGroup.apps.map((app) => (
                                      <Clickable
                                        key={app.clientAppId}
                                        onClick={() =>
                                          handleApplicationClick(
                                            tenantGroup.clientId,
                                            app.clientAppId,
                                          )
                                        }
                                        outline="xs"
                                        stretch
                                      >
                                        <div className={classes.truncatedText}>
                                          <Resource
                                            name={app.productName}
                                            subtext={app.vendorName}
                                            domain={app.productDomainName}
                                            signedLogoUrl={app.signedLogoUrl}
                                          />
                                        </div>
                                      </Clickable>
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {isEmpty && !!debouncedSearch && (
                            <Box direction="column" gap={8}>
                              <Text
                                size={12}
                                weight={600}
                                color="textSecondary"
                              >
                                No results found
                              </Text>
                            </Box>
                          )}

                          {isEmpty && !debouncedSearch && (
                            <Box direction="column" gap={8}>
                              <Text
                                size={12}
                                weight={600}
                                color="textSecondary"
                              >
                                Search for anything...
                              </Text>
                            </Box>
                          )}
                        </Box>
                      </FloatingList>
                    </div>
                  </ScrollBox>
                </Card>
              </div>
            </FloatingFocusManager>
          )}
        </GlobalSearchContext.Provider>
      </FloatingPortal>
    </>
  );
};
